import classNames from 'classnames';
import { forwardRef, type JSX, MouseEventHandler, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import ChevronSm from '../../icons/ChevronSm';
import Close from '../../icons/Close';
import { Size } from '../../types';
import { IconDefinition } from '../IconRenderer';
import styles from './styles/styles.module.scss';

// https://www.figma.com/file/9Y1n3VoMJAcTGPe82bvySj/Denali-Components?node-id=8814%3A7611&mode=dev

type FilterChipProps = {
  className?: string;
  hideChevron?: boolean;
  icon?: IconDefinition<'orientation'>;
  isOpen?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onClear?: MouseEventHandler<HTMLButtonElement>;
  size?: Size<'sm' | 'md'>;
  testId?: string;
  selectedItems?: ReactNode[];
  unselectedText: ReactNode;
  truncate?: boolean;
};

const FilterChip = (
  { className, hideChevron, icon, isOpen, onClick, onClear, size = 'md', testId, selectedItems, unselectedText, truncate }: FilterChipProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element => {
  const intl = useIntl();
  const isSelected = selectedItems && selectedItems.length > 0;
  const text = isSelected ? selectedItems[0] : unselectedText;
  const selectedOverflowCount = isSelected && selectedItems.length > 1 ? `+${selectedItems.length - 1}` : null;
  const noChevron = hideChevron || isSelected;
  const buttonClasses = classNames(styles.button, styles[size], { [styles.noChevron]: noChevron });
  return (
    <div className={classNames(styles.container, className, { [styles.selected]: isSelected, [styles.truncate]: truncate })} ref={ref}>
      <button className={buttonClasses} onClick={onClick} data-testid={testId} type="button">
        {icon && <icon.Component size={size} orientation={icon.orientation} />}
        <div className={styles.textContent}>
          <span>{text}</span>
          {selectedOverflowCount && <span>{selectedOverflowCount}</span>}
        </div>
        {noChevron ? null : <ChevronSm orientation={isOpen ? 'up' : 'down'} color="currentColor" size={size} />}
      </button>
      {isSelected ? (
        <button
          className={buttonClasses}
          onClick={onClear}
          data-testid={`${testId}-clear`}
          type="button"
          title={intl.formatMessage({ defaultMessage: 'Close' })}
          aria-label={intl.formatMessage({ defaultMessage: 'Close' })}
        >
          <Close color="currentColor" size={size} />
        </button>
      ) : null}
    </div>
  );
};

export default forwardRef(FilterChip);
